import { BookingCompressed, IUserWorkplacesParameters, UserScheduler, UserService, UserWorkplaces } from '@jooxter/api';
import { flatMap, uniqBy } from 'lodash-es';
import { WorkplaceQueryKeys } from '../queryKeys';
import { WorkplaceStaleTimeEnum } from '../staleTimes';
import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { AxiosError, isAxiosError } from 'axios';

export const useFetchOldUserScheduler = (
  options: IUserWorkplacesParameters
): { schedulers: Array<UserScheduler & { bookings?: BookingCompressed[] }> | undefined } => {
  const fetchScheduler = () => UserService.getScheduler(options);

  const { data: schedulers } = useQuery({
    queryKey: [WorkplaceQueryKeys.GetOldScheduler, options],
    queryFn: () => fetchScheduler(),
    placeholderData: keepPreviousData,
    staleTime: WorkplaceStaleTimeEnum.GetOldScheduler,
  });

  return { schedulers };
};

/**
 * This uses the users/workplaces endpoint
 * It will complete the response so that everyday of the week
 * is filled with a workplace. If no workplace exists for a day,
 * it is set to type UNKNOWN.
 */
export const useFetchWorkplaces = (
  options: Omit<IUserWorkplacesParameters, 'from' | 'to'> & { from?: DateTime; to?: DateTime },
  withSlice = false,
  personalWorkplaces = false,
  hasSaturdayWeek = false
): {
  workplaces: UserWorkplaces[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: AxiosError | null;
  fetchNextPage: (options?: FetchNextPageOptions | undefined) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<
        {
          data: UserWorkplaces[];
          nextPage: string;
        },
        unknown
      >
    >
  >;
  hasNextPage?: boolean;
  lastPage: UserWorkplaces[];
  isFetching: boolean;
} => {
  const [workplaces, setWorkplaces] = useState<UserWorkplaces[]>([]);
  const [lastPage, setLastPage] = useState<UserWorkplaces[]>([]);

  const fetchWorkplaces = async ({ pageParam = '' }) => {
    if (!(options.from && options.to)) {
      return Promise.reject(null);
    }

    const newOptions: IUserWorkplacesParameters = { ...options } as IUserWorkplacesParameters;

    if (pageParam) {
      newOptions.page = pageParam;
    }

    return await UserService.getWorkplaces(newOptions);
  };

  const key = options.favoritesOnly
    ? WorkplaceQueryKeys.GetFavoritesWorkplaces
    : personalWorkplaces
      ? WorkplaceQueryKeys.GetMyWorkplaces
      : WorkplaceQueryKeys.GetWorkplaces;

  const {
    data,
    isLoading,
    isError,
    error: unknownError,
    isFetching,
    isRefetching,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [key, options],
    queryFn: fetchWorkplaces,
    refetchInterval: 30000,
    getNextPageParam: (lastPage: { nextPage?: string; data: UserWorkplaces[] }) => {
      // must return undefined if it's the last page
      // lastPage.nextPage is ''
      return lastPage.nextPage?.length && lastPage.nextPage.length > 0 ? lastPage.nextPage : undefined;
    },
    initialPageParam: '',
    staleTime: WorkplaceStaleTimeEnum.GetWorkplaces,
    placeholderData: keepPreviousData,
    enabled: !!(options.from && options.to),
  });

  const error = unknownError
    ? isAxiosError(unknownError)
      ? unknownError
      : new AxiosError(unknownError.message)
    : null;

  useEffect(() => {
    if (data?.pages.length) {
      const pages = [...data.pages];
      setLastPage(data.pages[pages.length - 1].data);

      const workplaces = uniqBy(flatMap(pages.map((page) => page.data)), 'id');

      if (withSlice) {
        setWorkplaces(
          workplaces.map((workplace) => ({
            ...workplace,
            workplaces: workplace.workplaces?.slice(0, hasSaturdayWeek ? 6 : 5),
          }))
        );
      } else {
        setWorkplaces(workplaces);
      }
    }
  }, [data?.pages, withSlice]);

  return {
    workplaces,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    lastPage,
    isFetching: isRefetching || isFetching,
  };
};

export const useInvalidateWorkplaceQueries = () => {
  const queryClient = useQueryClient();

  const invalidateWorkplaceQueries = useCallback(() => {
    queryClient.invalidateQueries({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      predicate: (query: any): boolean => {
        return (
          query.queryKey[0] === WorkplaceQueryKeys.GetWorkplaces ||
          query.queryKey[0] === WorkplaceQueryKeys.GetFavoritesWorkplaces ||
          query.queryKey[0] === WorkplaceQueryKeys.GetMyWorkplaces ||
          query.queryKey[0] === WorkplaceQueryKeys.GetOldScheduler
        );
      },
    });
  }, [queryClient]);

  return { invalidateWorkplaceQueries };
};
